import type { Coach as CoachModel } from '../model/Coach';
import type { MatchResult, PlayoffStructure, PostseasonSeedSnapshotRow } from '../model/Season';
import {
    buildSingleEliminationBracket,
    computePlayoffSeeds,
    implicitBracketPaddingByes,
    type BracketMatchView,
    type BracketSide,
    type PlayoffSeedRow
} from './playoffBracketBuild';
import { isUsablePostseasonSnapshot } from './postseasonFinalize';
import { getStandingsThroughWeek, lastRegularSeasonWeekIndex } from './seasonStandingsCore';

export type PlayoffResultsMap = Record<string, MatchResult>;

export type SeasonDataForPlayoffs = {
    divisions: { divisionName: string; teams: { teamId: string; teamName: string; coachId: string; race: string }[] }[];
    weeks: { weekName: string; matches: { homeTeamId: string; awayTeamId: string; results?: MatchResult }[] }[];
    playoffStructure?: PlayoffStructure;
    postseasonSeedsSnapshot?: PostseasonSeedSnapshotRow[];
};

const VISITING = Symbol('visiting');

function seedRowToBracketSide(row: PlayoffSeedRow): BracketSide {
    return {
        kind: 'team',
        teamId: row.teamId,
        teamName: row.teamName,
        coachName: row.coachName,
        seed: row.seed
    };
}

/**
 * True when we should use the “bye teams wait out round 1; later rounds re-pair by locked seed” layout.
 * Falls back to the classic power-of-two bracket when this geometry is invalid (e.g. would leave a non-power-of-two survivor count).
 */
export function shouldUseReseedByePlayoffFormat(seeds: PlayoffSeedRow[], byeCount: number): boolean {
    if (byeCount <= 0 || seeds.length < 2) return false;
    const playing = seeds.length - byeCount;
    if (playing < 2 || playing % 2 !== 0) return false;
    const afterOpening = byeCount + playing / 2;
    return afterOpening >= 2 && Number.isInteger(Math.log2(afterOpening));
}

/** Total playoff rounds once the reseed bracket is complete (e.g. 6 teams, 2 byes → 3). */
export function expectedReseedPlayoffRoundCount(seeds: PlayoffSeedRow[], byeCount: number): number {
    if (!shouldUseReseedByePlayoffFormat(seeds, byeCount)) return 0;
    let survivors = byeCount + (seeds.length - byeCount) / 2;
    let count = 1;
    while (survivors > 1) {
        survivors /= 2;
        count++;
    }
    return count;
}

/**
 * Effective first-round bye count for the reseed bracket.
 * Many seasons store `byeCount: 0` even though the classic bracket pads to a power of two (e.g. 6 teams → 2 byes);
 * invalid configured values (e.g. 3 byes with 6 teams) fall back to that same implicit count when it yields a valid bracket.
 */
export function resolveByeCountForReseedBracket(seeds: PlayoffSeedRow[], configuredByeCount: number): number {
    if (seeds.length < 2) return Math.max(0, configuredByeCount);
    if (shouldUseReseedByePlayoffFormat(seeds, configuredByeCount)) return configuredByeCount;
    const implicit = implicitBracketPaddingByes(seeds.length);
    if (implicit > 0 && shouldUseReseedByePlayoffFormat(seeds, implicit)) return implicit;
    return configuredByeCount;
}

/** Honors `bracketMatchup`; only applies implicit bye resolution for `lowestVsHighest` (the default). */
export function resolveBracketByeCount(
    seeds: PlayoffSeedRow[],
    playoffStructure: Pick<PlayoffStructure, 'byeCount' | 'bracketMatchup'> | undefined
): number {
    const configured = playoffStructure?.byeCount ?? 0;
    const matchup = playoffStructure?.bracketMatchup ?? 'lowestVsHighest';
    if (matchup !== 'lowestVsHighest') return configured;
    return resolveByeCountForReseedBracket(seeds, configured);
}

export function matchByIdFromRounds(rounds: BracketMatchView[][]): Map<string, BracketMatchView> {
    const m = new Map<string, BracketMatchView>();
    for (const row of rounds) {
        for (const match of row) {
            m.set(match.id, match);
        }
    }
    return m;
}

/** Seeds for bracket: locked snapshot when present, else from standings through last RS week. */
export function getPlayoffSeedRowsForBracket(seasonData: SeasonDataForPlayoffs, leagueCoaches: CoachModel[]): PlayoffSeedRow[] {
    if (isUsablePostseasonSnapshot(seasonData.postseasonSeedsSnapshot)) {
        return seasonData.postseasonSeedsSnapshot!.map(r => ({ ...r }));
    }
    const ps = seasonData.playoffStructure;
    if (!ps || typeof ps.divisionWinnersPerDivision !== 'number') return [];
    const lastRs = lastRegularSeasonWeekIndex(seasonData.weeks);
    if (lastRs < 0) return [];
    const standings = getStandingsThroughWeek(seasonData, leagueCoaches, lastRs);
    const allTeamsFlat = Object.values(standings).flat();
    return computePlayoffSeeds(ps, standings, allTeamsFlat);
}

export function buildBracketRoundsFromSeason(
    seasonData: SeasonDataForPlayoffs,
    leagueCoaches: CoachModel[],
    playoffResults?: PlayoffResultsMap
): BracketMatchView[][] {
    const seeds = getPlayoffSeedRowsForBracket(seasonData, leagueCoaches);
    const byeCount = resolveBracketByeCount(seeds, seasonData.playoffStructure);
    if (shouldUseReseedByePlayoffFormat(seeds, byeCount)) {
        return buildReseedEliminationRounds(seeds, byeCount, playoffResults);
    }
    return buildSingleEliminationBracket(seeds);
}

/**
 * Resolved participant: real team id, bye, still waiting on feeder, or impossible.
 */
export type ResolvedParticipant = { kind: 'team'; teamId: string } | { kind: 'bye' } | { kind: 'pending' };

export function resolveParticipant(
    side: BracketSide,
    matchById: Map<string, BracketMatchView>,
    playoffResults: PlayoffResultsMap | undefined,
    winnerMemo: Map<string, string | null | typeof VISITING>
): ResolvedParticipant {
    if (side.kind === 'bye') return { kind: 'bye' };
    if (side.kind === 'team') return { kind: 'team', teamId: side.teamId };
    const feeder = matchById.get(side.matchId);
    if (!feeder) return { kind: 'pending' };
    const w = getWinnerTeamId(feeder, matchById, playoffResults, winnerMemo);
    if (!w) return { kind: 'pending' };
    return { kind: 'team', teamId: w };
}

export function getWinnerTeamId(
    match: BracketMatchView,
    matchById: Map<string, BracketMatchView>,
    playoffResults: PlayoffResultsMap | undefined,
    winnerMemo: Map<string, string | null | typeof VISITING>
): string | null {
    const prev = winnerMemo.get(match.id);
    if (prev === VISITING) return null;
    if (typeof prev === 'string') return prev;
    if (prev === null) return null;

    winnerMemo.set(match.id, VISITING);

    const pr = playoffResults ?? {};
    const homeP = resolveParticipant(match.home, matchById, playoffResults, winnerMemo);
    const awayP = resolveParticipant(match.away, matchById, playoffResults, winnerMemo);

    if (homeP.kind === 'pending' || awayP.kind === 'pending') {
        winnerMemo.set(match.id, null);
        return null;
    }

    if (homeP.kind === 'bye' && awayP.kind === 'bye') {
        winnerMemo.set(match.id, null);
        return null;
    }
    if (homeP.kind === 'bye' && awayP.kind === 'team') {
        winnerMemo.set(match.id, awayP.teamId);
        return awayP.teamId;
    }
    if (awayP.kind === 'bye' && homeP.kind === 'team') {
        winnerMemo.set(match.id, homeP.teamId);
        return homeP.teamId;
    }

    const homeId = homeP.kind === 'team' ? homeP.teamId : '';
    const awayId = awayP.kind === 'team' ? awayP.teamId : '';
    const res = pr[match.id];
    if (!res) {
        winnerMemo.set(match.id, null);
        return null;
    }
    const hs = res.homeTeamScore ?? 0;
    const as = res.awayTeamScore ?? 0;
    if (hs > as) {
        winnerMemo.set(match.id, homeId);
        return homeId;
    }
    if (as > hs) {
        winnerMemo.set(match.id, awayId);
        return awayId;
    }
    winnerMemo.set(match.id, null);
    return null;
}

/**
 * Single elimination with locked seeds: among teams playing this round, lowest seed faces highest seed.
 * First playoff round includes only seeds (byeCount+1)…n; top `byeCount` seeds wait until quarterfinal winners exist.
 * Later rounds appear only after every match in the previous round has a recorded winner (pairings then recomputed by seed).
 */
export function buildReseedEliminationRounds(
    seeds: PlayoffSeedRow[],
    byeCount: number,
    playoffResults: PlayoffResultsMap | undefined
): BracketMatchView[][] {
    if (!shouldUseReseedByePlayoffFormat(seeds, byeCount)) {
        return buildSingleEliminationBracket(seeds);
    }

    const sorted = [...seeds].sort((a, b) => a.seed - b.seed);
    const rounds: BracketMatchView[][] = [];
    let remaining = sorted;
    let roundIdx = 0;
    let isFirst = true;

    while (remaining.length > 1) {
        let byeSlice: PlayoffSeedRow[] = [];
        let active: PlayoffSeedRow[];
        if (isFirst && byeCount > 0) {
            byeSlice = remaining.slice(0, byeCount);
            active = remaining.slice(byeCount);
            isFirst = false;
        } else {
            active = remaining;
        }

        if (active.length === 0 || active.length % 2 !== 0) {
            break;
        }

        const half = active.length / 2;
        const matches: BracketMatchView[] = [];
        for (let i = 0; i < half; i++) {
            const rowLo = active[i];
            const rowHi = active[active.length - 1 - i];
            matches.push({
                id: `r${roundIdx}-m${i}`,
                roundIndex: roundIdx,
                matchIndex: i,
                home: seedRowToBracketSide(rowLo),
                away: seedRowToBracketSide(rowHi)
            });
        }
        rounds.push(matches);

        const matchById = matchByIdFromRounds(rounds);
        const winners: PlayoffSeedRow[] = [...byeSlice];
        let allResolved = true;
        for (const m of matches) {
            const memo = new Map<string, string | null | typeof VISITING>();
            const w = getWinnerTeamId(m, matchById, playoffResults, memo);
            if (!w) {
                allResolved = false;
                break;
            }
            const row = sorted.find(s => s.teamId === w);
            if (!row) {
                allResolved = false;
                break;
            }
            winners.push(row);
        }

        if (!allResolved) break;

        winners.sort((a, b) => a.seed - b.seed);
        remaining = winners;
        roundIdx++;
    }

    return rounds;
}

/** Structural home/away team ids when both are real teams (not bye, not pending). */
export function getResolvedStructuralTeamIds(
    match: BracketMatchView,
    matchById: Map<string, BracketMatchView>,
    playoffResults: PlayoffResultsMap | undefined
): { homeTeamId: string; awayTeamId: string } | null {
    const memo = new Map<string, string | null | typeof VISITING>();
    const h = resolveParticipant(match.home, matchById, playoffResults, memo);
    const a = resolveParticipant(match.away, matchById, playoffResults, memo);
    if (h.kind !== 'team' || a.kind !== 'team') return null;
    return { homeTeamId: h.teamId, awayTeamId: a.teamId };
}

/**
 * Find the playoff bracket match for two team ids (unordered). Prefers earliest round, then matchIndex.
 * Only considers matches where both sides resolve to real teams (not bye).
 */
export function findPlayoffSlotForTeamPair(
    seasonData: SeasonDataForPlayoffs,
    leagueCoaches: CoachModel[],
    teamIdA: string,
    teamIdB: string,
    playoffResults: PlayoffResultsMap | undefined
): { matchId: string; homeTeamId: string; awayTeamId: string } | null {
    const seeds = getPlayoffSeedRowsForBracket(seasonData, leagueCoaches);
    if (seeds.length === 0) return null;
    const byeCount = resolveBracketByeCount(seeds, seasonData.playoffStructure);
    const rounds = shouldUseReseedByePlayoffFormat(seeds, byeCount)
        ? buildReseedEliminationRounds(seeds, byeCount, playoffResults)
        : buildSingleEliminationBracket(seeds);
    const matchById = matchByIdFromRounds(rounds);

    for (let ri = 0; ri < rounds.length; ri++) {
        const row = rounds[ri];
        for (let mi = 0; mi < row.length; mi++) {
            const match = row[mi];
            const structural = getResolvedStructuralTeamIds(match, matchById, playoffResults);
            if (!structural) continue;
            const mset = new Set([structural.homeTeamId, structural.awayTeamId]);
            if (mset.size !== 2 || !mset.has(teamIdA) || !mset.has(teamIdB)) continue;
            return {
                matchId: match.id,
                homeTeamId: structural.homeTeamId,
                awayTeamId: structural.awayTeamId
            };
        }
    }
    return null;
}

/** For UI: expand winnerOf placeholders to team sides when the feeder winner is known. */
export function resolveSideForDisplay(
    side: BracketSide,
    matchById: Map<string, BracketMatchView>,
    playoffResults: PlayoffResultsMap | undefined,
    seedByTeamId: Map<string, PlayoffSeedRow>
): BracketSide {
    const memo = new Map<string, string | null | typeof VISITING>();
    const r = resolveParticipant(side, matchById, playoffResults, memo);
    if (r.kind === 'bye') return { kind: 'bye' };
    if (r.kind === 'pending') return side;
    if (side.kind === 'team') return side;
    const meta = seedByTeamId.get(r.teamId);
    if (meta) {
        return {
            kind: 'team',
            teamId: meta.teamId,
            teamName: meta.teamName,
            coachName: meta.coachName,
            seed: meta.seed
        };
    }
    return {
        kind: 'team',
        teamId: r.teamId,
        teamName: r.teamId,
        coachName: '',
        seed: 0
    };
}

/** Parse `playoffResults` from season JSON (API / import). */
export function parsePlayoffResultsFromSeasonJson(parsed: unknown): PlayoffResultsMap | undefined {
    if (!parsed || typeof parsed !== 'object') return undefined;
    const raw = (parsed as Record<string, unknown>).playoffResults;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
    const out: PlayoffResultsMap = {};
    for (const [k, v] of Object.entries(raw)) {
        if (!v || typeof v !== 'object') continue;
        const r = v as Record<string, unknown>;
        if (typeof r.homeTeamScore !== 'number' || typeof r.awayTeamScore !== 'number') continue;
        const homeCas = typeof r.homeTeamCasInflicted === 'number' ? r.homeTeamCasInflicted : 0;
        const awayCas = typeof r.awayTeamCasInflicted === 'number' ? r.awayTeamCasInflicted : 0;
        out[k] = {
            ...(v as MatchResult),
            overtime: Boolean(r.overtime),
            homeTeamCasInflicted: homeCas,
            awayTeamCasInflicted: awayCas
        };
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
